import "./App.css";
import "./lib/install";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/LandingV3";
import How from "./pages/How";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";

const PublicCheck = lazy(() => import("./pages/PublicCheck"));
const WhoOwns = lazy(() => import("./pages/WhoOwns"));
const Public401k = lazy(() => import("./pages/Public401k"));
const PublicFundGrade = lazy(() => import("./pages/PublicFundGrade"));
const PublicReceipts = lazy(() => import("./pages/PublicReceipts"));
const PublicReads = lazy(() => import("./pages/PublicReads"));
const PublicRead = lazy(() => import("./pages/PublicRead"));
const PublicBook = lazy(() => import("./pages/PublicBook"));
const PublicTheme = lazy(() => import("./pages/PublicTheme"));
const PublicSignals = lazy(() => import("./pages/PublicSignals"));
const Radar = lazy(() => import("./pages/Radar"));
const PublicBlueprint = lazy(() => import("./pages/PublicBlueprint"));
const Discover = lazy(() => import("./pages/app/Discover"));
const CurveBasket = lazy(() => import("./pages/app/CurveBasket"));
const Desk = lazy(() => import("./pages/Desk"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const Filers = lazy(() => import("./pages/Filers"));
const FilerPage = lazy(() => import("./pages/FilerPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const XRay = lazy(() => import("./pages/app/XRay"));
const Workspace = lazy(() => import("./pages/app/Workspace"));
const LpFees = lazy(() => import("./pages/LpFees"));
const LpGrade = lazy(() => import("./pages/LpGrade"));
const Methodology = lazy(() => import("./pages/Methodology"));

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const PublicBooks = lazy(() => import("./pages/PublicBooks"));
const PublicSpotlight = lazy(() => import("./pages/PublicSpotlight"));
const Feed = lazy(() => import("./pages/app/Feed"));
const Stocks = lazy(() => import("./pages/app/Stocks"));
const Sectors = lazy(() => import("./pages/app/Sectors"));
const Themes = lazy(() => import("./pages/app/Themes"));
const ThemePage = lazy(() => import("./pages/app/ThemePage"));
const StockPage = lazy(() => import("./pages/app/StockPage"));
const FundPage = lazy(() => import("./pages/app/FundPage"));
const FundGrades = lazy(() => import("./pages/app/FundGrades"));
const Morning = lazy(() => import("./pages/app/Morning"));
const ReceiptsPage = lazy(() => import("./pages/app/ReceiptsPage"));
const Upgrade = lazy(() => import("./pages/app/Upgrade"));
const Welcome = lazy(() => import("./pages/app/Welcome"));
const Watchlist = lazy(() => import("./pages/app/Watchlist"));
const RecordPage = lazy(() => import("./pages/app/RecordPage"));
const Admin = lazy(() => import("./pages/app/Admin"));
const Account = lazy(() => import("./pages/app/Account"));
const ManagerPage = lazy(() => import("./pages/app/ManagerPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center font-mono text-sm text-[#5A6272]">
        Checking your session…
      </div>
    );
  if (user === false)
    return <Navigate to={location.pathname === "/app/welcome" ? "/register" : "/login"} state={{ from: location.pathname + location.search + location.hash }} replace />;
  return children;
}

const Loading = () => (
  <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center font-mono text-sm text-[#5A6272]">
    Loading…
  </div>
);

function AppRoutes() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
            <Route path="/" element={window.location.hostname.startsWith("desk.") ? <Desk /> : <Landing />} />
            <Route path="/how" element={<How />} />
            <Route path="/is-it-too-late/:symbol" element={<PublicCheck mode="late" />} />
            <Route path="/should-i-sell/:symbol" element={<PublicCheck mode="sell" />} />
            <Route path="/who-owns/:symbol" element={<WhoOwns />} />
            <Route path="/check/:symbol" element={<PublicCheck mode="late" canonical />} />
            <Route path="/fund/:symbol" element={<PublicFundGrade canonical />} />
            <Route path="/for-professionals" element={<Navigate to="/desk" replace />} />
            <Route path="/desk" element={<Desk />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/read" element={<PublicReads />} />
            <Route path="/read/:edition" element={<PublicRead />} />
            <Route path="/book" element={<PublicBook />} />
            <Route path="/theme" element={<PublicTheme />} />
            <Route path="/theme/:themeId" element={<PublicTheme />} />
            <Route path="/signals" element={<PublicSignals />} />
            <Route path="/radar" element={<Radar />} />
            <Route path="/blueprint" element={<PublicBlueprint />} />
            <Route path="/app/blueprint" element={<Navigate to="/app#missing" replace />} />
            <Route path="/app/curve/:key" element={<Protected><CurveBasket /></Protected>} />
            <Route path="/thebook" element={<PublicBook />} />
            <Route path="/books" element={<PublicBooks />} />
            <Route path="/spotlight/:sid" element={<PublicSpotlight />} />
            <Route path="/filers" element={<Filers />} />
            <Route path="/filer/:slug" element={<FilerPage />} />
            <Route path="/compare/:a/:b" element={<ComparePage />} />
            <Route path="/app/xray" element={<XRay />} />
            <Route path="/app/workspace" element={<Protected><Workspace /></Protected>} />
            <Route path="/lp/fees" element={<LpFees />} />
            <Route path="/lp/grade" element={<LpGrade />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/how-crowded-is/:symbol" element={<PublicCheck mode="crowded" />} />
            <Route path="/how-crowded-is-my-401k" element={<Public401k />} />
            <Route path="/fund-grade/:symbol" element={<PublicFundGrade />} />
            <Route path="/x" element={<Navigate to="/?utm_source=x&utm_medium=profile" replace />} />
            <Route path="/proof" element={<Navigate to="/book?utm_source=x&utm_campaign=pin" replace />} />
            <Route path="/v2" element={<Navigate to="/" replace />} />
            <Route path="/v3" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/app" element={<Protected><Watchlist /></Protected>} />
            <Route path="/app/feed" element={<Protected><Feed /></Protected>} />
            <Route path="/app/stocks" element={<Navigate to="/app/discover" replace />} />
            <Route path="/app/discover" element={<Protected><Discover tab="stocks" /></Protected>} />
            <Route path="/app/discover/themes" element={<Protected><Discover tab="themes" /></Protected>} />
            <Route path="/app/sectors" element={<Protected><Sectors /></Protected>} />
            <Route path="/app/themes" element={<Navigate to="/app/discover/themes" replace />} />
            <Route path="/app/theme/:slug" element={<Protected><ThemePage /></Protected>} />
            <Route path="/app/stock/:symbol" element={<Protected><StockPage /></Protected>} />
            <Route path="/app/fund/:symbol" element={<Protected><FundPage /></Protected>} />
            <Route path="/app/funds" element={<Protected><FundGrades /></Protected>} />
            <Route path="/app/manager" element={<Protected><ManagerPage /></Protected>} />
            <Route path="/app/receipts" element={<Protected><ReceiptsPage /></Protected>} />
            <Route path="/app/upgrade" element={<Protected><Upgrade /></Protected>} />
            <Route path="/app/welcome" element={<Protected><Welcome /></Protected>} />
            <Route path="/app/watchlist" element={<Navigate to="/app" replace />} />
            <Route path="/account" element={<Navigate to="/app/account" replace />} />
            <Route path="/billing" element={<Navigate to="/app/account" replace />} />
            <Route path="/settings" element={<Navigate to="/app/account" replace />} />
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            <Route path="/upgrade" element={<Navigate to="/app/upgrade" replace />} />
            <Route path="/record" element={<Navigate to={{ pathname: "/", hash: "#record" }} replace />} />
            <Route path="/receipts" element={<PublicReceipts />} />
            <Route path="/how-it-works" element={<Navigate to="/how" replace />} />
            <Route path="/401k" element={<Navigate to="/how-crowded-is-my-401k" replace />} />
            <Route path="/feed" element={<Navigate to="/app/feed" replace />} />
            <Route path="/app/weekly" element={<Protected><Morning /></Protected>} />
            <Route path="/app/morning" element={<Navigate to="/app/weekly" replace />} />
            <Route path="/app/book" element={<Navigate to="/app/record" replace />} />
            <Route path="/app/record" element={<Protected><RecordPage /></Protected>} />
            <Route path="/app/account" element={<Protected><Account /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
            <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
